<template>
  <v-chip
    :color="computedColor"
    v-bind="$attrs"
    small
    label
    :text-color="textColor"
    class="table-status-badge"
    :class="`table-status-badge--${computedStatusClass}`"
  >
    <v-icon v-if="showIcon" left x-small :color="textColor">
      {{ computedIcon }}
    </v-icon>
    <slot>{{ status }}</slot>
  </v-chip>
</template>

<script lang="ts">
import Vue from 'vue'
import type { TableStatusBadgeProps } from './types'

export default Vue.extend<any, any, any, TableStatusBadgeProps>({
  name: 'TableStatusBadge',
  inheritAttrs: false,
  props: {
    status: {
      type: String,
      required: true
    },
    showIcon: {
      type: Boolean,
      default: true
    },
    colorMap: {
      type: Object,
      default: () => ({
        active: 'success',
        enabled: 'success',
        inactive: 'grey',
        disabled: 'grey',
        pending: 'warning',
        error: 'error'
      })
    },
    iconMap: {
      type: Object,
      default: () => ({
        active: 'mdi-check-circle',
        enabled: 'mdi-check-circle',
        inactive: 'mdi-pause-circle',
        disabled: 'mdi-close-circle',
        pending: 'mdi-clock-outline',
        error: 'mdi-alert-circle'
      })
    },
    textColor: {
      type: String,
      default: 'white'
    }
  },
  computed: {
    normalizedStatus(): string {
      return this.status.toLowerCase()
    },
    computedColor(): string {
      return this.colorMap[this.normalizedStatus] || 'default'
    },
    computedIcon(): string {
      return this.iconMap[this.normalizedStatus] || 'mdi-circle'
    },
    computedStatusClass(): string {
      // Map Vuetify colors to semantic status classes
      const colorToStatusMap: Record<string, string> = {
        'success': 'success',
        'error': 'error',
        'warning': 'warning',
        'info': 'info',
        'grey': 'inactive',
        'default': 'default'
      }
      return colorToStatusMap[this.computedColor] || 'default'
    }
  }
})
</script>

<style scoped>
/* Override Vuetify's v-chip colours with design token CSS variables for dark mode support */
.table-status-badge--success {
  background-color: var(--success-light) !important;
  color: var(--success) !important;
  border: none;
}

.table-status-badge--success >>> .v-icon {
  color: var(--success) !important;
}

.table-status-badge--error {
  background-color: var(--danger-light) !important;
  color: var(--danger) !important;
  border: none;
}

.table-status-badge--error >>> .v-icon {
  color: var(--danger) !important;
}

.table-status-badge--warning {
  background-color: var(--warning-light) !important;
  color: var(--warning) !important;
  border: none;
}

.table-status-badge--warning >>> .v-icon {
  color: var(--warning) !important;
}

.table-status-badge--info {
  background-color: var(--info-light) !important;
  color: var(--info) !important;
  border: none;
}

.table-status-badge--info >>> .v-icon {
  color: var(--info) !important;
}

.table-status-badge--inactive {
  background-color: var(--muted) !important;
  color: var(--muted-foreground) !important;
  border: none;
}

.table-status-badge--inactive >>> .v-icon {
  color: var(--muted-foreground) !important;
}
</style>
